import { useEffect, useRef, useCallback } from 'react';
import analyticsTracker from '../utils/analyticsTracker';

/**
 * Custom hook for easy analytics integration
 */
export const useAnalytics = () => {
  return {
    trackEvent: analyticsTracker.trackEvent.bind(analyticsTracker),
    trackButtonClick: analyticsTracker.trackButtonClick.bind(analyticsTracker),
    trackFormFieldFocus: analyticsTracker.trackFormFieldFocus.bind(analyticsTracker),
    trackFormFieldBlur: analyticsTracker.trackFormFieldBlur.bind(analyticsTracker),
    trackFormSubmission: analyticsTracker.trackFormSubmission.bind(analyticsTracker),
    trackStepChange: analyticsTracker.trackStepChange.bind(analyticsTracker),
    trackCalendarInteraction: analyticsTracker.trackCalendarInteraction.bind(analyticsTracker),
    trackUniversitySlider: analyticsTracker.trackUniversitySlider.bind(analyticsTracker),
    trackTestimonialChange: analyticsTracker.trackTestimonialChange.bind(analyticsTracker),
    completeSession: analyticsTracker.completeSession.bind(analyticsTracker),
    getAnalytics: analyticsTracker.getAnalytics.bind(analyticsTracker),
    aggregateSessionData: analyticsTracker.aggregateSessionData.bind(analyticsTracker),
    clearAllData: analyticsTracker.clearAllData.bind(analyticsTracker)
  };
};

/**
 * Hook for tracking component visibility
 */
export const useComponentTracking = (componentName) => {
  const elementRef = useRef();
  const observerRef = useRef();
  const isVisibleRef = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting && entry.intersectionRatio > 0.1;

          if (isVisible !== isVisibleRef.current) {
            isVisibleRef.current = isVisible;
            analyticsTracker.trackComponentView(componentName, isVisible);
          }
        });
      },
      {
        threshold: [0, 0.1, 0.5, 1.0],
        rootMargin: '0px 0px -10% 0px'
      }
    );

    observer.observe(element);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Ensure we track the end of visibility on cleanup
      if (isVisibleRef.current) {
        analyticsTracker.trackComponentView(componentName, false);
      }
    };
  }, [componentName]);

  return elementRef;
};

/**
 * Hook for tracking form field interactions
 */
export const useFormFieldTracking = () => {
  const trackFieldFocus = useCallback((fieldName) => {
    analyticsTracker.trackFormFieldFocus(fieldName);
  }, []);

  const trackFieldBlur = useCallback((fieldName, value = '') => {
    analyticsTracker.trackFormFieldBlur(fieldName, value);
  }, []);

  const createFieldProps = useCallback((fieldName) => ({
    onFocus: () => trackFieldFocus(fieldName),
    onBlur: (e) => trackFieldBlur(fieldName, e.target.value)
  }), [trackFieldFocus, trackFieldBlur]);

  return {
    trackFieldFocus,
    trackFieldBlur,
    createFieldProps
  };
};

/**
 * Hook for tracking user journey steps
 */
export const useStepTracking = () => {
  const previousStepRef = useRef(null);

  const trackStep = useCallback((currentStep) => {
    if (previousStepRef.current && previousStepRef.current !== currentStep) {
      analyticsTracker.trackStepChange(previousStepRef.current, currentStep);
    }
    previousStepRef.current = currentStep;
  }, []);

  return trackStep;
};

/**
 * Hook for tracking button clicks with automatic context
 */
export const useButtonTracking = () => {
  const trackClick = useCallback((buttonName, additionalContext = {}) => {
    const context = {
      timestamp: Date.now(),
      currentUrl: window.location.href,
      currentHash: window.location.hash,
      ...additionalContext
    };

    analyticsTracker.trackButtonClick(buttonName, context);
  }, []);

  return trackClick;
};